import React, { MouseEvent, useCallback, useMemo, useState } from "react";

import Col from "antd/es/col";
import Row from "antd/es/row";
import { useIntl } from "react-intl";

import FieldCategory from "@mapmycustomers/shared/enum/fieldModel/FieldCategory";
import FieldFeature from "@mapmycustomers/shared/enum/fieldModel/FieldFeature";
import { EntityTypesSupportedColumnSelection } from "@mapmycustomers/shared/types/entity";
import IField from "@mapmycustomers/shared/types/fieldModel/IField";
import ColumnModel from "@mapmycustomers/shared/types/viewModel/internalModel/ColumnModel";
import { SearchField } from "@mapmycustomers/ui";

import Checkbox from "@app/component/input/Checkbox";
import layout from "@app/styles/layout";
import ColumnVisibility from "@app/types/viewModel/ColumnVisibility";
import getFieldModelByEntityType from "@app/util/fieldModel/getByEntityType";
import withRemountOnAccessChange from "@app/util/react/withRemountOnAccessChange";

import styles from "./ColumnSelector.module.scss";
import ColumnTree from "./ColumnTree";

const categories: FieldCategory[] = [
  FieldCategory.REQUIRED,
  FieldCategory.NON_REQUIRED,
  FieldCategory.ADDRESS,
  FieldCategory.PARENT_COMPANY_ADDRESS_FIELDS,
  FieldCategory.COMPANY_ADDRESS_FIELDS,
  FieldCategory.PEOPLE_ADDRESS_FIELDS,
  FieldCategory.DEAL_ADDRESS_FIELDS,
  FieldCategory.RELATIONSHIPS,
  FieldCategory.DETAILS,
];

interface Props {
  columns: ColumnModel<IField>;
  columnTreeClassName?: string;
  entityType: EntityTypesSupportedColumnSelection;
  fieldFilter?: (field: IField) => boolean;
  fixedFields?: IField[];
  onUpdate: (visibilities: ColumnVisibility[]) => void;
  showCategories?: boolean;
  showSelectAllLabel?: boolean;
}

const ColumnSelector = ({
  columns,
  columnTreeClassName,
  entityType,
  fieldFilter,
  fixedFields,
  onUpdate,
  showCategories = true,
  showSelectAllLabel,
}: Props) => {
  const intl = useIntl();
  const [search, setSearch] = useState<string>("");

  const categorizedFields = useMemo(() => {
    const categorizedFieldsResult = getFieldModelByEntityType(entityType).categorizedFields;
    if (!fieldFilter) {
      return categorizedFieldsResult;
    }
    return Object.keys(categorizedFieldsResult).reduce(
      (data, key) => ({
        ...data,
        [key]: categorizedFieldsResult[key as FieldCategory].filter(fieldFilter),
      }),
      {}
    );
  }, [entityType, fieldFilter]);

  const handleSelect = useCallback(
    (fieldsNames: IField["name"][]) => {
      onUpdate(
        getFieldModelByEntityType(entityType).fields.map((field) => ({
          field,
          visible:
            fieldsNames.includes(field.name) || field.hasFeature(FieldFeature.ALWAYS_VISIBLE),
        }))
      );
    },
    [entityType, onUpdate]
  );

  const selectedFieldsNames = useMemo(
    () => columns.filter(({ visible }) => visible).map(({ field: { name } }) => name),
    [columns]
  );

  const areAllChecked = useMemo(
    () =>
      !columns
        .filter((column) => !fixedFields?.includes(column.field))
        .some(({ visible }) => !visible),
    [columns, fixedFields]
  );
  const areAnyChecked = useMemo(
    () =>
      !areAllChecked &&
      columns
        .filter((column) => !fixedFields?.includes(column.field))
        .some(({ visible }) => visible),
    [areAllChecked, columns, fixedFields]
  );

  const handleMainCheckBoxClick = useCallback(
    (e: MouseEvent<HTMLElement>) => {
      let visibility = !areAllChecked;
      if (e.altKey) {
        visibility = !visibility;
      }
      const fixedFieldsNames = new Set(fixedFields?.map(({ name }) => name) ?? []);
      onUpdate(
        getFieldModelByEntityType(entityType).fields.map((field) => ({
          field,
          // if fixed, then take current visibility from columns
          visible: fixedFieldsNames.has(field.name)
            ? (columns.find((column) => column.field.name === field.name)?.visible ?? true)
            : visibility || field.hasFeature(FieldFeature.ALWAYS_VISIBLE),
        }))
      );
    },
    [areAllChecked, columns, entityType, fixedFields, onUpdate]
  );

  return (
    <>
      <div className={styles.topControls}>
        <div className={styles.allSelector}>
          <Row align="middle" gutter={layout.spacerS}>
            <Col>
              <Checkbox
                checked={areAllChecked}
                indeterminate={areAnyChecked}
                onClick={handleMainCheckBoxClick}
              />
            </Col>
            {showSelectAllLabel && (
              <Col className={styles.allSelectorText}>
                {intl.formatMessage({
                  id: "component.selectColumnsPane.selectAll",
                  defaultMessage: "Select All",
                  description: "Select all text on select columns pane",
                })}
              </Col>
            )}
          </Row>
        </div>
        <SearchField
          className={styles.search}
          onChange={setSearch}
          placeholder={intl.formatMessage({
            id: "component.selectColumnsPane.search",
            defaultMessage: "Search",
            description: "Search text on select columns pane",
          })}
          size="middle"
          value={search}
        />
      </div>
      <ColumnTree
        categories={categories}
        categorizedFields={categorizedFields}
        className={columnTreeClassName}
        fixedFields={fixedFields}
        onChangeSelection={handleSelect}
        query={search.trim().toLowerCase()}
        selectedFieldsNames={selectedFieldsNames}
        showCategories={showCategories}
      />
    </>
  );
};

export default withRemountOnAccessChange(ColumnSelector);
